const DEFAULT_BRAND = 'Douglas & McCartney';
const DEFAULT_CANONICAL_ORIGIN = process.env.REACT_APP_CANONICAL_ORIGIN || 'https://www.douglasmccartney.com';
const TRACKING_PARAM_PATTERNS = ['utm_', 'gclid', 'fbclid', 'msclkid', 'dclid'];

function compactWhitespace(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function uniqueNonEmpty(values) {
  const seen = new Set();
  const result = [];
  for (const value of values) {
    const normalized = compactWhitespace(value);
    if (!normalized) continue;
    const key = normalized.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(normalized);
  }
  return result;
}

function scoreTitleLength(value) {
  const len = value.length;
  const target = 55;
  let score = Math.abs(len - target);
  if (len >= 50 && len <= 60) score -= 5;
  return score;
}

export function buildTitle({ pageTitle, brand = DEFAULT_BRAND, intent, location } = {}) {
  const normalizedPageTitle = compactWhitespace(pageTitle);
  const normalizedBrand = compactWhitespace(brand || DEFAULT_BRAND);
  const normalizedIntent = compactWhitespace(intent);
  const normalizedLocation = compactWhitespace(location);

  if (!normalizedPageTitle) return normalizedBrand;

  const pageWithLocation = uniqueNonEmpty([normalizedPageTitle, normalizedLocation]).join(' - ');
  const candidates = uniqueNonEmpty([
    [normalizedPageTitle, normalizedIntent, normalizedLocation, normalizedBrand].filter(Boolean).join(' | '),
    [normalizedPageTitle, normalizedIntent, normalizedBrand].filter(Boolean).join(' | '),
    [pageWithLocation, normalizedBrand].filter(Boolean).join(' | '),
    [normalizedPageTitle, normalizedBrand].filter(Boolean).join(' | '),
    normalizedPageTitle,
  ]);

  const targetFits = candidates.filter((title) => title.length <= 60);
  if (targetFits.length) {
    targetFits.sort((a, b) => scoreTitleLength(a) - scoreTitleLength(b));
    return targetFits[0];
  }

  candidates.sort((a, b) => a.length - b.length);
  return candidates[0] || normalizedBrand;
}

function trimAtWordBoundary(value, maxLength) {
  const normalized = compactWhitespace(value);
  if (!normalized || normalized.length <= maxLength) return normalized;
  const sliced = normalized.slice(0, maxLength + 1);
  const cutAt = sliced.lastIndexOf(' ');
  const base = cutAt > Math.floor(maxLength * 0.7) ? sliced.slice(0, cutAt) : normalized.slice(0, maxLength);
  return base.replace(/[,\s.;:!?-]+$/g, '').trim();
}

export function buildDescription({ summary, CTA, cta } = {}) {
  const normalizedSummary = compactWhitespace(summary);
  const normalizedCta = compactWhitespace(CTA || cta);
  if (!normalizedSummary) return '';
  if (!normalizedCta) return trimAtWordBoundary(normalizedSummary, 160);

  const combined = compactWhitespace(`${normalizedSummary}. ${normalizedCta}`).replace(/\.\./g, '.');
  if (combined.length <= 160) return combined;
  if (normalizedSummary.length >= 145 && normalizedSummary.length <= 160) return normalizedSummary;
  return trimAtWordBoundary(combined, 160);
}

function shouldStripTrackingParam(key) {
  const normalized = String(key || '').toLowerCase();
  return TRACKING_PARAM_PATTERNS.some((pattern) => (
    pattern.endsWith('_') ? normalized.startsWith(pattern) : normalized === pattern
  ));
}

function normalizePathname(pathname) {
  const raw = compactWhitespace(pathname || '/');
  let output = raw.startsWith('/') ? raw : `/${raw}`;
  output = output.replace(/\/{2,}/g, '/').toLowerCase();
  if (output.length > 1) {
    output = output.replace(/\/+$/g, '');
  }
  return output || '/';
}

export function buildCanonical(url, options = {}) {
  const canonicalOrigin = compactWhitespace(options.canonicalOrigin || DEFAULT_CANONICAL_ORIGIN) || DEFAULT_CANONICAL_ORIGIN;
  const allowedQueryParams = Array.isArray(options.allowedQueryParams) ? options.allowedQueryParams : [];
  let origin;
  try {
    origin = new URL(canonicalOrigin);
  } catch (_err) {
    origin = new URL(DEFAULT_CANONICAL_ORIGIN);
  }

  let parsed;
  try {
    parsed = new URL(String(url || '/'), origin.toString());
  } catch (_err) {
    parsed = new URL('/', origin.toString());
  }

  const canonical = new URL(origin.toString());
  canonical.pathname = normalizePathname(parsed.pathname);
  for (const [key, value] of parsed.searchParams.entries()) {
    if (shouldStripTrackingParam(key)) continue;
    if (!allowedQueryParams.includes(key)) continue;
    canonical.searchParams.append(key, value);
  }
  canonical.hash = '';
  return canonical.toString();
}

export function isLikelyNonProductionHost(hostname) {
  const host = String(hostname || '').toLowerCase();
  if (!host) return true;
  if (host === 'localhost' || host === '127.0.0.1') return true;
  return host.endsWith('.web.app') || host.endsWith('.firebaseapp.com');
}

export function normalizeRoutePath(pathname) {
  return normalizePathname(pathname);
}
